/**
 * Owner admin (F25): the private, read-only overview of the service for the
 * owner. One endpoint, E130 `GET /api/v1/admin/overview`, answered only for
 * the addresses in the Worker var ADMIN_EMAILS (and the dev sign-in locally);
 * everyone else gets 404 `not_found`.
 *
 * White-label (CONT-058): nothing here names an AI provider or model. AI
 * spend is reported by purpose and workspace, in US dollars.
 *
 * Implements ADM-001, ADM-010, ADM-020, ADM-030, ADM-040, ADM-050, ADM-055 and ADM-056.
 */
import type { BillingInterval, BillingPlan, SubscriptionStatus } from './billing';

export const ADMIN_ROUTES = {
  /** GET (E130; ADM-001). */
  adminOverview: '/api/v1/admin/overview',
} as const;

/** Most users listed; `users.total` counts them all. */
export const ADMIN_USER_LIST_LIMIT = 500;
/** Most failed runs listed, newest first. */
export const ADMIN_FAILED_RUN_LIMIT = 20;
/** Most workspaces listed under AI spend, by spend this month. */
export const ADMIN_SPEND_WORKSPACE_LIMIT = 50;

export interface AdminUser {
  id: string;
  name: string;
  /** Verified address, or null. */
  email: string | null;
  signedUpAt: string;
  /** The user's personal workspace's billing state; all null when it never subscribed. */
  plan: BillingPlan | null;
  interval: BillingInterval | null;
  quantity: number | null;
  status: SubscriptionStatus | null;
  trialEnd: string | null;
  cancelAtPeriodEnd: boolean;
  /** False for a Stripe test-mode subscription; null without one. */
  livemode: boolean | null;
}

export interface AdminUsers {
  total: number;
  /** Signed up in the 7 days before `generatedAt`. */
  newThisWeek: number;
  /** Newest first, at most ADMIN_USER_LIST_LIMIT. */
  list: AdminUser[];
}

export interface AdminSubscriptions {
  /** Live-mode subscriptions by status. */
  byStatus: Partial<Record<SubscriptionStatus, number>>;
  /** Monthly recurring revenue estimate in US dollars, before tax: live `active` and `past_due` subscriptions, yearly prices divided by 12. */
  mrrUsd: number;
  /** What live `trialing` subscriptions would add to MRR if every trial converted. */
  trialMrrUsd: number;
  /** Live subscriptions set to cancel at the end of their period (still counted above). */
  cancelling: number;
  /** Stripe test-mode subscriptions, left out of every figure above. */
  testMode: number;
}

export interface AdminPurposeSpend {
  /** The AI purpose (registry key), e.g. `screening_pass_a`. */
  purpose: string;
  monthUsd: number;
  calls: number;
}

export interface AdminWorkspaceSpend {
  /** Null for calls not attributed to a workspace (question drafts). */
  workspaceId: string | null;
  workspaceName: string | null;
  ownerEmail: string | null;
  todayUsd: number;
  monthUsd: number;
  callsMonth: number;
}

export interface AdminScreeningUsage {
  workspaceId: string;
  workspaceName: string;
  ownerEmail: string | null;
  /** Start of the workspace's latest allowance window with a counter (BILL-085). */
  windowStart: string;
  /** AI-screened records counted in that window. */
  used: number;
  /** The plan's allowance for the current window; null when the counter's window is not the current one or the workspace has no access. */
  allowance: number | null;
}

export interface AdminAiSpend {
  /** Since 00:00 UTC today. */
  todayUsd: number;
  /** Since 00:00 UTC on the first of this month. */
  monthUsd: number;
  callsToday: number;
  callsMonth: number;
  /** Calls this month with no recorded cost (failed before usage, or an unpriced model). */
  uncostedCallsMonth: number;
  /** Calls today that ended in an error. */
  failedCallsToday: number;
  /** AI-074: a workspace's daily spend cap. */
  workspaceDailyCapUsd: number;
  /** AI-075: the deployment-wide daily stop. */
  deploymentDailyCapUsd: number;
  byPurpose: AdminPurposeSpend[];
  /** By spend this month, at most ADMIN_SPEND_WORKSPACE_LIMIT. */
  byWorkspace: AdminWorkspaceSpend[];
  /** The screening budget counters (BILL-085), largest use first. */
  screening: AdminScreeningUsage[];
}

export interface AdminRun {
  pageSlug: string;
  pageTitle: string;
  ownerEmail: string | null;
  runId: string;
  status: 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';
  kind: 'full' | 'confirmation';
  /** The run's error, with any AI vendor or model name replaced by "AI". */
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
  /** When an active run counted as stalled (LIVE-122); null otherwise. */
  stalledSince: string | null;
}

export interface AdminRuns {
  /** Queued or running now. */
  activeCount: number;
  /** Failed runs, newest first, at most ADMIN_FAILED_RUN_LIMIT. */
  recentFailures: AdminRun[];
  /** Active runs that are stalled now (LIVE-122). */
  stalled: AdminRun[];
}

/** ADM-050, OBS-055: one stored unexpected error. No user, address or request body is kept. */
export interface AdminServerError {
  at: string;
  method: string;
  /** A route pattern such as /api/v1/evidence-pages/:slug, or scheduled:<job>. */
  route: string;
  status: number;
  errorName: string;
  /** Email addresses removed, AI provider and model names replaced by `AI`. */
  message: string;
  /** The reference the user was shown (`details.reference`). */
  reference: string;
}

/** ADM-050: unexpected server errors stored by OBS-055, kept 30 days. */
export interface AdminServerErrors {
  last24h: number;
  last7d: number;
  /** Newest first, at most ADMIN_SERVER_ERROR_LIMIT. */
  recent: AdminServerError[];
}

export const ADMIN_SERVER_ERROR_LIMIT = 20;

/**
 * ADM-055: what can stop Stripe's webhook working, in the order the page lists them.
 * - `secret_missing` / `secret_malformed`: STRIPE_WEBHOOK_SECRET is unset, or does not start `whsec_`;
 * - `key_missing` / `key_malformed`: STRIPE_SECRET_KEY is unset, or does not start `sk_` or `rk_`;
 * - `deliveries_refused`: a delivery failed its signature check after the last processed event (BILL-068);
 * - `no_events_after_checkout`: a checkout was confirmed on return (BILL-067) and no event followed.
 */
export type AdminWebhookProblem =
  | 'secret_missing'
  | 'secret_malformed'
  | 'key_missing'
  | 'key_malformed'
  | 'deliveries_refused'
  | 'no_events_after_checkout';

/** Minutes after a checkout confirmed on return within which the webhook must have delivered an event (ADM-055). */
export const ADMIN_WEBHOOK_SILENCE_MINUTES = 15;

/** ADM-055. Only the form of each secret is checked; no secret, or any part of one, is returned. */
export interface AdminWebhookHealth {
  secretSet: boolean;
  /** STRIPE_WEBHOOK_SECRET starts `whsec_`. */
  secretWellFormed: boolean;
  keySet: boolean;
  /** STRIPE_SECRET_KEY starts `sk_` or `rk_`. */
  keyWellFormed: boolean;
  /** From the key's prefix; null when the key is missing or malformed. */
  mode: 'test' | 'live' | null;
  lastEventAt: string | null;
  lastRefusedAt: string | null;
  lastRefusedProblem: 'no_match' | 'too_old' | null;
  lastReturnConfirmAt: string | null;
  problems: AdminWebhookProblem[];
}

/**
 * ADM-056: what stops account emails, in the order the page lists them.
 * - `key_missing` / `key_malformed`: RESEND_API_KEY is unset (email off), or does not start `re_`;
 * - `from_missing`: EMAIL_FROM is unset (email off);
 * - `webhook_secret_missing` / `webhook_secret_malformed`: RESEND_WEBHOOK_SECRET is unset, or does not start `whsec_`
 *   (sending works; bounces, complaints and unsubscribes do not arrive);
 * - `provider_rejecting`: queued emails are waiting because Resend answered 401 or 403 (MAIL-014).
 */
export type AdminEmailProblem = 'key_missing' | 'key_malformed' | 'from_missing' | 'webhook_secret_missing' | 'webhook_secret_malformed' | 'provider_rejecting';

/** ADM-056. Only the form of each secret is checked; no secret, or any part of one, is returned. */
export interface AdminEmailHealth {
  keySet: boolean;
  /** RESEND_API_KEY starts `re_`. */
  keyWellFormed: boolean;
  fromSet: boolean;
  webhookSecretSet: boolean;
  /** RESEND_WEBHOOK_SECRET starts `whsec_`. */
  webhookSecretWellFormed: boolean;
  /** Queued emails held by a 401 or 403 from Resend. */
  heldByProvider: number;
  /** The latest of those rejections: when, and '<status> <provider error name>'. */
  lastRejectedAt: string | null;
  lastRejectedError: string | null;
  /** Account emails sent in the 7 days before `generatedAt`. */
  sentLast7Days: number;
  problems: AdminEmailProblem[];
}

/** ALRT-077: following and alert email, as counts only; no person or page is identified. */
export interface AdminAlertUse {
  /** Users with at least one active subscription. */
  followers: number;
  activeSubscriptions: number;
  /** Alert emails sent in the 30 days before `generatedAt` (each alert, digests, monthly notes). */
  emailsSent30d: number;
  /** Visits that arrived from an alert email in those 30 days. */
  visits30d: number;
}

/** PAPI-061: one UTC day of public API use, as counts. */
export interface AdminApiDay {
  /** YYYY-MM-DD. */
  day: string;
  /** Documented-API requests without a session or key. */
  anonymous: number;
  /** Documented-API and MCP requests made with a key. */
  key: number;
  /** MCP tool calls. */
  mcp: number;
}

/** PAPI-061: use of the public read API and the MCP server, as counts. */
export interface AdminApiUse {
  /** Documented-API requests without a session or key, in the 30 days before `generatedAt`. */
  anonymousRequests30d: number;
  /** API keys not revoked and not expired at `generatedAt`. */
  activeKeys: number;
  /** Documented-API requests made with a key in those 30 days. */
  keyRequests30d: number;
  /** MCP tool calls in those 30 days. */
  mcpCalls30d: number;
  /** The 30 UTC days up to and including today, oldest first, zero where nothing happened. */
  daily: AdminApiDay[];
}

/** ADM-070: the steps of a review, in order. */
export const ACTIVATION_STEPS = ['signed_up', 'drafted_question', 'froze_protocol', 'ran_search', 'screened_records', 'evidence_page'] as const;
export type ActivationStep = (typeof ACTIVATION_STEPS)[number];

export interface AdminActivationStep {
  step: ActivationStep;
  /** Users who signed up in the last `windowDays` days and reached this step. */
  recent: number;
  /** All users who reached this step. */
  total: number;
}

/** ADM-070: how far users get, as counts only. `steps` follows ACTIVATION_STEPS. */
export interface AdminActivation {
  windowDays: number;
  steps: AdminActivationStep[];
}

export interface AdminOverviewResponse {
  generatedAt: string;
  activation: AdminActivation;
  api: AdminApiUse;
  webhook: AdminWebhookHealth;
  email: AdminEmailHealth;
  alerts: AdminAlertUse;
  users: AdminUsers;
  subscriptions: AdminSubscriptions;
  aiSpend: AdminAiSpend;
  runs: AdminRuns;
  serverErrors: AdminServerErrors;
}
